import React, { useEffect, useState } from 'react';
import { WhatsappLogo, X } from '@phosphor-icons/react';
import { useI18n } from '@/lib/i18n';

const PHONE = '4915208483930'; // +49 152 08483930 in international format (no +, no spaces)

export default function WhatsAppButton() {
  const { t, lang } = useI18n();
  const [openPanel, setOpenPanel] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  const presets = lang === 'en'
    ? [
        { label: t.whatsapp.preset1, msg: 'Hello De Melo Recruiting, I would like to request staff for an upcoming project.' },
        { label: t.whatsapp.preset2, msg: 'Hello De Melo Recruiting, I am a subcontractor and would like to be informed about new projects.' },
        { label: t.whatsapp.preset3, msg: 'Hello De Melo Recruiting, I would like to apply.' },
      ]
    : [
        { label: t.whatsapp.preset1, msg: 'Hallo De Melo Recruiting, ich möchte Personal für ein bevorstehendes Projekt anfragen.' },
        { label: t.whatsapp.preset2, msg: 'Hallo De Melo Recruiting, ich bin Subunternehmer und möchte über neue Aufträge informiert werden.' },
        { label: t.whatsapp.preset3, msg: 'Hallo De Melo Recruiting, ich möchte mich gerne bewerben.' },
      ];

  const open = (msg) => {
    const url = `https://wa.me/${PHONE}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setOpenPanel(false);
  };

  return (
    <>
      {openPanel && (
        <div
          data-testid="whatsapp-panel"
          className="fixed bottom-28 left-4 sm:left-6 z-[90] w-[320px] max-w-[calc(100vw-2rem)] bg-white border border-[#E2E8F0] shadow-2xl overflow-hidden animate-[fadeUp_0.3s_ease]"
        >
          <div className="bg-[#0A1128] text-white p-5 flex items-start gap-3 relative">
            <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
              <WhatsappLogo size={22} weight="fill" className="text-white" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-[15px]">De Melo Recruiting</div>
              <div className="text-white/60 text-xs mt-0.5">{t.whatsapp.status}</div>
            </div>
            <button
              onClick={() => setOpenPanel(false)}
              data-testid="whatsapp-close"
              className="text-white/50 hover:text-white transition-colors"
              aria-label="close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="p-5 bg-[#F8F9FA]">
            <div className="bg-white border border-[#E2E8F0] p-4 mb-4 text-[13px] text-[#1E2229]/80 leading-relaxed rounded-tr-2xl rounded-br-2xl rounded-bl-2xl">
              {t.whatsapp.greeting}
            </div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#64748B] mb-2">{t.whatsapp.choose}</div>
            <div className="space-y-2">
              {presets.map((p, i) => (
                <button
                  key={i}
                  onClick={() => open(p.msg)}
                  data-testid={`whatsapp-preset-${i}`}
                  className="w-full text-left bg-white hover:bg-[#0A1128] hover:text-white text-[#0A1128] border border-[#E2E8F0] hover:border-[#0A1128] px-4 py-3 text-[13px] font-medium transition-colors"
                >
                  {p.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => open(t.whatsapp.fallback)}
              data-testid="whatsapp-start"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebd5a] text-white px-5 py-3 text-[12px] font-semibold tracking-[0.18em] uppercase transition-colors"
            >
              <WhatsappLogo size={16} weight="fill" />
              {t.whatsapp.openChat}
            </button>
          </div>
        </div>
      )}

      <button
        data-testid="whatsapp-button"
        onClick={() => setOpenPanel((v) => !v)}
        aria-label="WhatsApp Chat"
        className="fixed bottom-6 left-4 sm:left-6 z-[90] w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1ebd5a] text-white shadow-2xl flex items-center justify-center transition-all hover:scale-105 group"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" />
        <WhatsappLogo size={28} weight="fill" className="relative" />
        <span className="absolute right-full mr-3 whitespace-nowrap bg-[#0A1128] text-white text-[11px] tracking-[0.18em] uppercase px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none hidden sm:block">
          {t.whatsapp.tooltip}
        </span>
      </button>
    </>
  );
}
